// Browser-side OKX public WebSocket client for live watchlist / order-ticket
// quotes. Same source the backend tick engine uses (wss://ws.okx.com), consumed
// client-side so we can stream arbitrary watchlist symbols without adding
// backend load or any API keys. `tickers` channel → last / bid / ask / 24h.
//
// Symbols are canonical base tickers ("BTC", "ETH"). instId = "BASE-USDT-SWAP".

const OKX_WS = "wss://ws.okx.com:8443/ws/v5/public";
const KEEPALIVE_MS = 15000; // OKX drops idle sockets >30s
const MAX_BACKOFF_MS = 30000;

export const toInstId = (base) => `${String(base).toUpperCase()}-USDT-SWAP`;
export const fromInstId = (instId) => {
  const p = String(instId).split("-");
  return p.length === 3 && p[2] === "SWAP" ? p[0] : null;
};

// Normalized quote shape emitted to subscribers.
function parseTicker(d) {
  const last = parseFloat(d.last);
  const open24h = parseFloat(d.open24h);
  const chg = last - open24h;
  const chgPct = open24h ? (chg / open24h) * 100 : 0;
  return {
    symbol: fromInstId(d.instId),
    last,
    bid: parseFloat(d.bidPx),
    ask: parseFloat(d.askPx),
    open24h,
    high24h: parseFloat(d.high24h),
    low24h: parseFloat(d.low24h),
    vol24h: parseFloat(d.vol24h), // base-currency volume
    volCcy24h: parseFloat(d.volCcy24h),
    chg,
    chgPct,
    ts: Number(d.ts),
  };
}

// A single shared connection, reference-counted per symbol. Components subscribe
// to the symbols they need; the client subscribes/unsubscribes on OKX as the
// union changes and multiplexes ticks to all listeners.
class OkxFeed {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.refCounts = new Map(); // base -> count
    this.listeners = new Set(); // fn(quote)
    this.backoff = 1000;
    this.keepaliveTimer = null;
    this.lastQuotes = new Map(); // base -> quote (snapshot for late subscribers)
    this.shouldRun = false;
  }

  onQuote(fn) {
    this.listeners.add(fn);
    // replay last-known snapshots so a new listener paints immediately
    for (const q of this.lastQuotes.values()) fn(q);
    return () => this.listeners.delete(fn);
  }

  subscribe(symbols) {
    const added = [];
    for (const raw of symbols) {
      const base = String(raw).toUpperCase();
      const n = this.refCounts.get(base) || 0;
      this.refCounts.set(base, n + 1);
      if (n === 0) added.push(base);
    }
    if (added.length) {
      this.shouldRun = true;
      this._ensureConnected();
      this._send("subscribe", added);
    }
    return () => this.unsubscribe(symbols);
  }

  unsubscribe(symbols) {
    const removed = [];
    for (const raw of symbols) {
      const base = String(raw).toUpperCase();
      const n = this.refCounts.get(base) || 0;
      if (n <= 1) {
        this.refCounts.delete(base);
        removed.push(base);
      } else {
        this.refCounts.set(base, n - 1);
      }
    }
    if (removed.length) this._send("unsubscribe", removed);
    if (this.refCounts.size === 0) this._teardown();
  }

  _args(bases) {
    return bases.map((b) => ({ channel: "tickers", instId: toInstId(b) }));
  }

  _send(op, bases) {
    if (!this.ws || this.ws.readyState !== 1 || !bases.length) return;
    try {
      this.ws.send(JSON.stringify({ op, args: this._args(bases) }));
    } catch {
      /* will resubscribe on reconnect */
    }
  }

  _ensureConnected() {
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
    if (typeof WebSocket === "undefined") return; // SSR / test guard
    const ws = new WebSocket(OKX_WS);
    this.ws = ws;

    ws.onopen = () => {
      this.connected = true;
      this.backoff = 1000;
      // (re)subscribe to the full current set
      const all = [...this.refCounts.keys()];
      if (all.length) this._send("subscribe", all);
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = setInterval(() => {
        try {
          if (ws.readyState === 1) ws.send("ping");
        } catch {}
      }, KEEPALIVE_MS);
    };

    ws.onmessage = (evt) => {
      const raw = evt.data;
      if (raw === "pong") return;
      let msg;
      try {
        msg = JSON.parse(raw);
      } catch {
        return;
      }
      if (msg.event) return; // subscribe/error acks
      if (!msg.data || !msg.arg || msg.arg.channel !== "tickers") return;
      for (const d of msg.data) {
        const q = parseTicker(d);
        if (!q.symbol) continue;
        this.lastQuotes.set(q.symbol, q);
        for (const fn of this.listeners) {
          try {
            fn(q);
          } catch {}
        }
      }
    };

    ws.onclose = () => {
      this.connected = false;
      clearInterval(this.keepaliveTimer);
      if (this.shouldRun && this.refCounts.size > 0) {
        setTimeout(() => this._ensureConnected(), this.backoff);
        this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
      }
    };

    ws.onerror = () => {
      try {
        ws.close();
      } catch {}
    };
  }

  _teardown() {
    this.shouldRun = false;
    clearInterval(this.keepaliveTimer);
    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
    }
    this.ws = null;
    this.connected = false;
  }

  isConnected() {
    return this.connected;
  }
}

// module-level singleton
export const okxFeed = new OkxFeed();
export default okxFeed;
